import { getOr, isNull, isUndefined } from 'lodash/fp';
import React, { useCallback, useMemo, useState } from 'react';

import { ClientRiskConversation, DocumentDatasetRiskTolerance, DocumentInstanceRiskField, TableInstanceRiskField } from '../../../../datasets/instances/store';
import { DatasetType } from '../../../../datasets/store';
import { Button } from '../../../../shared/button/Button';
import { IconButton } from '../../../../shared/button/IconButton';
import { SpeechBubble } from '../../../../shared/icons';
import { HorizontalWeightingSlider } from '../../../../shared/risk-tolerance/Slider';
import { Scrollable } from '../../../../shared/scrollable/Scrollable';
import { OverflowTooltip } from '../../../../shared/tooltip';
import styles from '../../Analytics.module.scss';
import { DocumentRiskBreakdown } from '../../store/types';
import { ClientRiskConversationModal, SelectedClientRiskConversation } from './ClientRiskConversationModal';
import { documentRiskLevelLabel } from './DocumentRiskToleranceLevel';
import { getFieldValueElement } from './utils';

const { white } = styles;

const ConversationIndicator: React.FC = () => (
    <div className={styles.conversationIndicator}>...</div>
);

interface DocumentRiskToleranceBreakdownProps {
    documentRiskFields: DocumentDatasetRiskTolerance[] | undefined;
    height: number;
    width: number;
    setShowBreakdown: () => void;
    showOverrideRiskRating: boolean;
}

export const DocumentRiskToleranceBreakdown: React.FC<DocumentRiskToleranceBreakdownProps> = ({ documentRiskFields, height, width, setShowBreakdown, showOverrideRiskRating }) => {
    const [selectedConversation, setSelectedConversation] = useState<SelectedClientRiskConversation | null>(null);
    const testId = 'document-risk-tolerance-analytics-document-breakdown-field';
    const getFieldInformation = useCallback((datasetId: number, datasetType: DatasetType, riskField: DocumentInstanceRiskField, rowId?: string, rowLabel?: string): DocumentRiskBreakdown => {
        const { label, weighting, fieldId, riskLevel, clientRiskConversation, type, sectionId, value } = riskField;
        const valueElement = getFieldValueElement(value, riskField, label, testId);
        const subId = rowId ? { rowId, rowLabel } : { sectionId };
        const overrideRiskLevel = getOr(riskLevel, 'overrideRiskLevel', clientRiskConversation[clientRiskConversation.length - 1]);
        return ({
            datasetType,
            datasetId,
            fieldId,
            fieldType: type,
            label,
            valueElement,
            weighting,
            riskLevel,
            overrideRiskLevel,
            clientRiskConversation,
            ...subId
        });
    }, []);

    const breakdown = useMemo(() => {
        if (isUndefined(documentRiskFields)) {
            return null;
        }
        const sortField = showOverrideRiskRating ? 'overrideRiskLevel' : 'riskLevel';
        const breakdown = documentRiskFields.reduce((acc: DocumentRiskBreakdown[], documentRiskField) => {
            const { datasetId, includedFields, type } = documentRiskField;
            if (type === DatasetType.FORM) {
                (includedFields as DocumentInstanceRiskField[]).forEach(field => {
                    const riskField = getFieldInformation(datasetId, type, field);
                    acc.push(riskField);
                });
            } else {
                const tableRiskFields = (includedFields as TableInstanceRiskField[])[0].riskFields;
                tableRiskFields.forEach((_, riskFieldIndex) => {
                    (includedFields as TableInstanceRiskField[]).forEach(field => {
                        const { rowId, riskFields, rowLabel } = field;
                        const riskField = riskFields[riskFieldIndex];
                        const tableRiskField = getFieldInformation(datasetId, type, riskField, rowId, rowLabel);
                        acc.push(tableRiskField);
                    });
                });
            }
            return acc;
        }, []).sort((a, b) => b[sortField] - a[sortField] || b.weighting - a.weighting);
        return breakdown;
    }, [getFieldInformation, documentRiskFields, showOverrideRiskRating]);

    const getDocumentRiskLevel = useCallback((riskLevel: number, overrideRiskLevel: number) => documentRiskLevelLabel(riskLevel, overrideRiskLevel, showOverrideRiskRating), [showOverrideRiskRating]);

    const breakdownWidth = useMemo(() => width / 2, [width]);
    const breakdownHeight = useMemo(() => height - 80, [height]);

    const conversationWidth = useMemo(() => breakdownWidth - 20, [breakdownWidth]);
    const conversationHeight = useMemo(() => breakdownHeight, [breakdownHeight]);

    const openClientConversation = useCallback((clientRiskConversation: ClientRiskConversation[], fieldLabel: string) => setSelectedConversation({ clientRiskConversation, fieldLabel }), []);
    const closeClientConversation = useCallback(() => setSelectedConversation(null), []);

    if (isNull(breakdown)) {
        return null;
    }

    return (
        <>
            <div className={styles.breakdownWrapper} style={{ height: `${breakdownHeight}px`, width: `${breakdownWidth}px` }}>
                <Scrollable height={`${breakdownHeight - 45}px`}>
                    {breakdown.map((field, index) => {
                        const { riskLevel, rowLabel, valueElement, weighting, fieldId, label, overrideRiskLevel, clientRiskConversation } = field;
                        const uniqueFieldId = `${fieldId}-${index}`;
                        const { colour, borderColour } = getDocumentRiskLevel(riskLevel, overrideRiskLevel);
                        const border = `2px solid ${borderColour.length > 0 ? borderColour : colour}`;
                        return (
                            <div key={uniqueFieldId} className={styles.riskFieldWrapper} style={{ border }}>
                                <div className={styles.riskLevel} style={{ backgroundColor: colour }}>
                                    {clientRiskConversation.length > 0 && <IconButton icon={SpeechBubble} onClick={() => openClientConversation(clientRiskConversation, label)} color={white} indicator={<ConversationIndicator />} showIndicator />}
                                </div>
                                <div className={styles.fieldAndWeightWrapper}>
                                    <div className={styles.fieldWrapper}>
                                        <OverflowTooltip className={styles.fieldLabel} overlayText={label} testId={`${testId}-label`} />
                                        <div className={styles.documentAnswerWrapper}>
                                            <div className={styles.fieldAnswerWrapper}>
                                                <div className={styles.fieldAnswerLabel}>Document Value:</div>
                                                {!isUndefined(rowLabel) && <div className={styles.rowLabel}>({rowLabel})</div>}
                                                {valueElement}
                                            </div>
                                        </div>
                                    </div>
                                    <HorizontalWeightingSlider weighting={weighting} />
                                </div>
                            </div>
                        );
                    })}
                </Scrollable>
                <Button label='Back' onClick={setShowBreakdown} />
            </div>
            {!isNull(selectedConversation) && <ClientRiskConversationModal closeModal={closeClientConversation} selectedConversation={selectedConversation} modalWidth={conversationWidth} modalHeight={conversationHeight} initialXOffset={20} />}
        </>
    );
};
